import { gsap } from "gsap";
import {
  ArrowDownToLine,
  ArrowRight,
  ArrowUpRight,
  CircleDollarSign,
  CreditCard,
  FileSpreadsheet,
  Lock,
  type LucideIcon,
  ShieldCheck,
  Users,
  Wallet,
} from "lucide-react";
import { type ReactNode, useLayoutEffect, useRef } from "react";

import { ColocaSuccessMark } from "@/shared/components/motion/ColocaSuccessMark";
import {
  MOTION_DURATION,
  MOTION_EASE,
  MOTION_RISE,
  MOTION_STAGGER,
} from "@/shared/components/motion/motion.tokens";
import { usePrefersReducedMotion } from "@/shared/hooks/usePrefersReducedMotion";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { VerificationPurpose } from "../types/verification-api.types";
import type { VerificationWelcomeShortcutId } from "../utils/verificationWelcome";

export interface VerificationWelcomeShortcut {
  id: VerificationWelcomeShortcutId;
  href: string;
  title: string;
  description: string;
}

export interface VerificationWelcomeProps {
  /** KYC (a person) or KYB (a company): only the sentence naming what was approved changes. */
  purpose: VerificationPurpose | null;
  /** Already filtered to what the user may open; see `useVerificationWelcomeShortcuts`. */
  shortcuts: readonly VerificationWelcomeShortcut[];
  onSelect: (shortcut: VerificationWelcomeShortcut) => void;
  /**
   * The full moment — sparks around the check and every shortcut unlocking in turn — for the first time the
   * reader learns of the approval. `false` for a return visit: the content still enters, quietly.
   */
  celebrate?: boolean;
  /** `dialog` drops the eyebrow, which the dialog's title already says. */
  variant?: "page" | "dialog";
  /**
   * Under the shortcuts: the screen's one primary action on the page. The shortcuts are equal choices drawn
   * as cards, never buttons competing with it; a dialog puts its actions in its own footer instead.
   */
  footer?: ReactNode;
}

const SHORTCUT_ICONS: Record<VerificationWelcomeShortcutId, LucideIcon> = {
  payins: ArrowDownToLine,
  payouts: ArrowUpRight,
  cards: CreditCard,
  wallets: Wallet,
  security: ShieldCheck,
  crypto: CircleDollarSign,
  payout_batches: FileSpreadsheet,
  clients: Users,
};

interface Spark {
  angle: number;
  distance: number;
  shape: "dot" | "bar";
  tone: string;
}

/** A fixed burst — the same every time, so the moment reads as designed rather than random. */
const SPARKS: readonly Spark[] = Array.from({ length: 12 }, (_, index) => ({
  angle: (index / 12) * Math.PI * 2 + (index % 2 === 0 ? 0.12 : -0.08),
  distance: index % 3 === 0 ? 50 : index % 3 === 1 ? 40 : 46,
  shape: index % 2 === 0 ? "dot" : "bar",
  tone: ["bg-primary", "bg-success", "bg-chart-category-2"][index % 3] ?? "bg-primary",
}));

/**
 * "You can use the platform now": the approved verification, told once, with the doors it opened.
 *
 * The signature is the shortcuts unlocking — each one enters behind a lock that gives way to its own icon,
 * after the check has landed — because that is literally what verification did: payouts, collections and
 * cards were held behind it. Everything is set in place by markup; GSAP only plays the from-states, so
 * reduced motion or a failed script shows the finished screen, never an empty one.
 */
export function VerificationWelcome({
  purpose,
  shortcuts,
  onSelect,
  celebrate = true,
  variant = "page",
  footer,
}: VerificationWelcomeProps) {
  const { t } = useTranslations("verification");
  const reduced = usePrefersReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const animateSparks = celebrate && !reduced;

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root || reduced) return;
    const ctx = gsap.context(() => {
      const q = gsap.utils.selector(root);
      const timeline = gsap.timeline();

      if (celebrate) {
        q("[data-spark]").forEach((spark, index) => {
          const { angle, distance } = SPARKS[index] ?? SPARKS[0];
          const at = 0.35 + (index % 4) * 0.02;
          timeline
            .set(spark, { x: 0, y: 0, scale: 0.4, rotation: 0 }, 0)
            .to(spark, { opacity: 1, duration: 0.15, ease: MOTION_EASE.enter }, at)
            .to(
              spark,
              {
                x: Math.cos(angle) * distance,
                y: Math.sin(angle) * distance,
                scale: 1,
                rotation: index % 2 === 0 ? 0 : 140,
                duration: MOTION_DURATION.enter,
                ease: MOTION_EASE.enter,
              },
              at,
            )
            .to(
              spark,
              { opacity: 0, scale: 0.3, duration: MOTION_DURATION.quick, ease: "power1.in" },
              at + MOTION_DURATION.enter,
            );
        });
      }

      timeline.fromTo(
        q("[data-reveal]"),
        { opacity: 0, y: MOTION_RISE },
        {
          opacity: 1,
          y: 0,
          duration: MOTION_DURATION.enter,
          ease: MOTION_EASE.enter,
          stagger: MOTION_STAGGER.base,
          clearProps: "transform,opacity",
        },
        0.2,
      );

      const tilesAt = celebrate ? 0.55 : 0.3;
      q("[data-tile]").forEach((tile, index) => {
        const at = tilesAt + index * MOTION_STAGGER.loose;
        timeline.fromTo(
          tile,
          { opacity: 0, y: MOTION_RISE },
          {
            opacity: 1,
            y: 0,
            duration: MOTION_DURATION.enter,
            ease: MOTION_EASE.enter,
            clearProps: "transform,opacity",
          },
          at,
        );
        if (!celebrate) return;
        // The lock gives way to what it was holding back.
        const lock = tile.querySelector("[data-lock]");
        const shade = tile.querySelector("[data-lock-shade]");
        const icon = tile.querySelector("[data-icon]");
        const unlockAt = at + MOTION_DURATION.enter * 0.55;
        if (shade) {
          timeline.fromTo(
            shade,
            { opacity: 1 },
            { opacity: 0, duration: MOTION_DURATION.quick, ease: MOTION_EASE.enter },
            unlockAt,
          );
        }
        if (lock) {
          timeline.fromTo(
            lock,
            { opacity: 1, scale: 1, y: 0 },
            { opacity: 0, scale: 0.6, y: -4, duration: MOTION_DURATION.quick, ease: "power1.in" },
            unlockAt,
          );
        }
        if (icon) {
          timeline.fromTo(
            icon,
            { opacity: 0, scale: 0.5 },
            { opacity: 1, scale: 1, duration: MOTION_DURATION.enter * 0.7, ease: MOTION_EASE.pop },
            unlockAt + 0.08,
          );
        }
      });
    }, root);
    return () => ctx.revert();
    // Mount only: a shortcut that arrives later (a feature flag read after the first render) simply appears,
    // rather than replaying the whole entrance under the reader.
  }, [reduced, celebrate]);

  const description = purpose === "KYC" ? t("welcome_description_kyc") : t("welcome_description_kyb");

  return (
    <div
      ref={ref}
      className={cn("flex flex-col items-center text-center", variant === "dialog" ? "gap-5 pt-4" : "gap-6")}
      aria-live={variant === "page" ? "polite" : undefined}
    >
      <div className="relative flex size-20 items-center justify-center">
        {animateSparks ? (
          <span aria-hidden="true" className="pointer-events-none absolute inset-0">
            {SPARKS.map((spark, index) => (
              <span
                key={index}
                data-spark
                className={cn(
                  "absolute opacity-0",
                  spark.tone,
                  spark.shape === "dot" ? "size-1.5 rounded-full" : "h-1 w-2.5 rounded-sm",
                )}
                // Centred by offset, not by a translate class: GSAP owns `transform` while it plays.
                style={
                  spark.shape === "dot"
                    ? { left: "calc(50% - 3px)", top: "calc(50% - 3px)" }
                    : { left: "calc(50% - 5px)", top: "calc(50% - 2px)" }
                }
              />
            ))}
          </span>
        ) : null}
        <ColocaSuccessMark className="size-16" />
      </div>

      <div className="max-w-md space-y-2">
        {variant === "page" ? (
          <p data-reveal className="text-xs font-semibold tracking-wide text-primary uppercase">
            {t("welcome_eyebrow")}
          </p>
        ) : null}
        <h2 data-reveal className="text-2xl font-semibold text-balance text-foreground">
          {t("welcome_title")}
        </h2>
        <p data-reveal className="text-sm text-pretty text-muted-foreground">
          {description}
        </p>
      </div>

      {shortcuts.length > 0 ? (
        // Columns follow the space the welcome is given, not the viewport: the onboarding card and the dialog are
        // both narrower than the screen.
        <div className="@container w-full space-y-3 text-left">
          <p data-reveal className="text-center text-sm font-medium text-foreground">
            {t("welcome_choose")}
          </p>
          <ul aria-label={t("welcome_shortcuts_label")} className="grid gap-3 @min-[30rem]:grid-cols-2">
            {shortcuts.map((shortcut) => (
              <WelcomeShortcutTile key={shortcut.id} shortcut={shortcut} onSelect={onSelect} />
            ))}
          </ul>
        </div>
      ) : null}

      {footer ? (
        <div data-reveal className="w-full">
          {footer}
        </div>
      ) : null}
    </div>
  );
}

function WelcomeShortcutTile({
  shortcut,
  onSelect,
}: {
  shortcut: VerificationWelcomeShortcut;
  onSelect: (shortcut: VerificationWelcomeShortcut) => void;
}) {
  const Icon = SHORTCUT_ICONS[shortcut.id];
  return (
    <li data-tile className="flex">
      <button
        type="button"
        onClick={() => onSelect(shortcut)}
        className={cn(
          "group flex w-full items-start gap-3 rounded-xl border border-border bg-card p-4 text-left",
          "transition-[transform,box-shadow,border-color] duration-200 ease-out",
          "hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md",
          "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none",
          "motion-reduce:transition-none motion-reduce:hover:translate-y-0",
        )}
      >
        <span className="relative flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-primary/10 text-primary">
          {/* Locked state, hidden by default: only the entrance shows it, then lifts it. */}
          <span data-lock-shade aria-hidden="true" className="absolute inset-0 bg-muted opacity-0" />
          <Lock data-lock aria-hidden="true" className="absolute size-4 text-muted-foreground opacity-0" />
          <Icon data-icon aria-hidden="true" className="relative size-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-foreground">{shortcut.title}</span>
          <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
            {shortcut.description}
          </span>
        </span>
        <ArrowRight
          aria-hidden="true"
          className={cn(
            "mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform duration-200",
            "group-hover:translate-x-0.5 group-hover:text-primary motion-reduce:transition-none",
          )}
        />
      </button>
    </li>
  );
}
